import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useI18n } from '../../i18n/I18nContext';
import api from '../../utils/api';

function AdminVendors() {
  const { user } = useAuth();
  const { t } = useI18n();

  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ email: '', password: '', name: '', contact_email: '', contact_phone: '', line_id: '', payout_terms: '', active: true });

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('name');

  useEffect(() => {
    if (user) loadData();
  }, [user]);

  async function loadData() {
    try {
      const data = await api.get('/admin/vendors');
      setVendors(data);
    } catch (err) {
      console.error('Failed to load:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/admin/vendors/${editingId}`, form);
      } else {
        await api.post('/admin/vendors', form);
      }
      setShowForm(false);
      setEditingId(null);
      setForm({ email: '', password: '', name: '', contact_email: '', contact_phone: '', line_id: '', payout_terms: '', active: true });
      loadData();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  }

  function handleEdit(vendor) {
    setForm({
      email: vendor.email,
      password: '',
      name: vendor.name,
      contact_email: vendor.contact_email || '',
      contact_phone: vendor.contact_phone || '',
      line_id: vendor.line_id || '',
      payout_terms: vendor.payout_terms || '',
      active: vendor.active
    });
    setEditingId(vendor.id);
    setShowForm(true);
  }

  // Filter and sort vendors
  const filteredVendors = vendors
    .filter(v => {
      const matchesSearch = !searchTerm ||
        v.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        v.email.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = filterStatus === 'all' ||
        (filterStatus === 'active' && v.active) ||
        (filterStatus === 'inactive' && !v.active);
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      if (sortBy === 'email') return a.email.localeCompare(b.email);
      if (sortBy === 'offers') return (b.offer_count || 0) - (a.offer_count || 0);
      return 0;
    });

  if (loading) {
    return <div className="flex justify-center" style={{ padding: '40px 0' }}><div className="spinner"></div></div>;
  }

  return (
    <div>
      {/* Page Header */}
      <div className="flex justify-between items-center mb-4">
        <h1 style={{ fontSize: '20px', fontWeight: '600' }}>{t('admin.vendors')}</h1>
        <button onClick={() => { setShowForm(true); setEditingId(null); setForm({ email: '', password: '', name: '', contact_email: '', contact_phone: '', line_id: '', payout_terms: '', active: true }); }} className="btn btn-primary" style={{ padding: '8px 16px', fontSize: '14px' }}>
          + {t('common.new')}
        </button>
      </div>
        {/* Filters */}
        <div className="card mb-4">
          <div className="flex gap-2 flex-wrap items-end">
            <div style={{ flex: '2', minWidth: '200px' }}>
              <label className="label text-xs">{t('common.search')}</label>
              <input
                type="text"
                className="input"
                placeholder={t('admin.searchByNameEmail')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div style={{ flex: '1', minWidth: '120px' }}>
              <label className="label text-xs">{t('common.status')}</label>
              <select className="input" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                <option value="all">{t('common.all')}</option>
                <option value="active">{t('common.active')}</option>
                <option value="inactive">{t('common.inactive')}</option>
              </select>
            </div>
            <div style={{ flex: '1', minWidth: '120px' }}>
              <label className="label text-xs">{t('common.sortBy')}</label>
              <select className="input" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="name">{t('admin.nameAZ')}</option>
                <option value="email">{t('common.email')}</option>
                <option value="offers">{t('admin.mostOffers')}</option>
              </select>
            </div>
          </div>
          <div className="text-secondary text-xs mt-2">
            {t('common.showing', { count: filteredVendors.length, total: vendors.length })} {t('admin.vendors').toLowerCase()}
          </div>
        </div>

        {showForm && (
          <div className="card mb-4">
            <h3 style={{ fontSize: '16px', marginBottom: '16px' }}>{editingId ? t('admin.editVendor') : t('admin.newVendor')}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="label">{t('admin.loginEmail')} *</label>
                <input type="email" className="input" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} required disabled={!!editingId} />
              </div>
              {!editingId && (
                <div className="form-group">
                  <label className="label">{t('common.password')} *</label>
                  <input type="password" className="input" value={form.password} onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))} required />
                </div>
              )}
              <div className="form-group">
                <label className="label">{t('admin.businessName')} *</label>
                <input type="text" className="input" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label className="label">{t('admin.contactEmail')}</label>
                <input type="email" className="input" value={form.contact_email} onChange={(e) => setForm(f => ({ ...f, contact_email: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="label">{t('admin.contactPhone')}</label>
                <input type="text" className="input" value={form.contact_phone} onChange={(e) => setForm(f => ({ ...f, contact_phone: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="label">{t('admin.lineId')}</label>
                <input type="text" className="input" value={form.line_id} onChange={(e) => setForm(f => ({ ...f, line_id: e.target.value }))} placeholder="e.g., @shopname or username" />
              </div>
              <div className="form-group">
                <label className="label">{t('admin.payoutTerms')}</label>
                <textarea className="input" rows={2} value={form.payout_terms} onChange={(e) => setForm(f => ({ ...f, payout_terms: e.target.value }))} />
              </div>
              <div className="form-group">
                <label><input type="checkbox" checked={form.active} onChange={(e) => setForm(f => ({ ...f, active: e.target.checked }))} /> {t('common.active')}</label>
              </div>
              <div className="flex gap-2">
                <button type="submit" className="btn btn-primary">{t('common.save')}</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>{t('common.cancel')}</button>
              </div>
            </form>
          </div>
        )}

        {filteredVendors.length === 0 ? (
          <div className="card text-center text-secondary">
            {t('admin.noVendorsFound')}
          </div>
        ) : (
          filteredVendors.map(v => (
            <div key={v.id} className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div className="flex items-center gap-2">
                  <h3 style={{ fontSize: '14px' }}>{v.name}</h3>
                  {!v.active && <span className="badge" style={{ background: 'var(--error)', color: 'white', fontSize: '10px', padding: '2px 6px' }}>{t('common.inactive')}</span>}
                </div>
                <p className="text-secondary text-xs">{v.email} • {v.offer_count || 0} {t('admin.offers').toLowerCase()}</p>
                {v.line_id && <p className="text-secondary text-xs">LINE: {v.line_id}</p>}
              </div>
              <button onClick={() => handleEdit(v)} className="btn btn-secondary" style={{ padding: '6px 10px', fontSize: '12px' }}>{t('common.edit')}</button>
            </div>
          ))
        )}
    </div>
  );
}

export default AdminVendors;
